import React, { useState } from 'react';
import { useOrganization } from '../../contexts/OrganizationContext';
import * as WechatAuthService from '../../services/WechatAuthService';
import { showError, logError, logInfo } from '../../utils/errorHandler';
import wechatIcon from '../../assets/icons/wechat.svg';
import './AuthLoginView.css';

interface AuthLoginViewProps {
  onAgreementClick: () => void;
  context?: 'mine' | 'payment' | 'team';
  paymentInfo?: {
    projectId: string;
    amount: number;
    recurring: boolean;
    anonymous: boolean;
    teamId?: string;
  };
  teamInfo?: {
    projectId: string;
  };
}

/**
 * 授权登录页面
 */
export const AuthLoginView: React.FC<AuthLoginViewProps> = ({ 
  onAgreementClick, 
  context = 'mine',
  paymentInfo,
  teamInfo
}) => {
  const { organization } = useOrganization();
  const [agreed, setAgreed] = useState(false);

  const handleWechatLogin = () => {
    console.log('[AuthLoginView] handleWechatLogin called', {
      agreed,
      isWechat: WechatAuthService.isWechat(),
      hasAppId: !!organization?.wechatAppId,
      appId: organization?.wechatAppId,
      context,
      hasPaymentInfo: !!paymentInfo,
      timestamp: new Date().toISOString()
    });
    
    if (!agreed) {
      console.log('[AuthLoginView] User not agreed, showing error');
      showError('请先阅读并同意用户协议', 'AuthLoginView.handleWechatLogin');
      return;
    }

    if (!WechatAuthService.isWechat()) {
      console.log('[AuthLoginView] Not in WeChat environment, showing error');
      showError('当前不在微信环境中', 'AuthLoginView.handleWechatLogin');
      return;
    }

    const appId = organization?.wechatAppId;
    if (!appId) {
      console.error('[AuthLoginView] AppID missing');
      logError('[AuthLoginView] AppID missing');
      showError('微信授权配置缺失：机构未配置 wechatAppId', 'AuthLoginView.handleWechatLogin');
      return;
    }

    logInfo('[AuthLoginView] All checks passed, starting redirect process', {
      context,
      hasPaymentInfo: !!paymentInfo,
      appId
    });

    // 确保已保存当前tab状态（如果还没有保存的话）
    // 支付场景的tab状态应该在设置showAuthLoginForPayment时已保存
    // "我的"场景的tab状态应该在MineView显示授权登录时已保存
    const savedTab = localStorage.getItem('pending_active_tab');
    if (!savedTab && context === 'mine') {
      // "我的"场景，如果没有保存，说明是从其他地方跳转过来的，保存为MINE
      localStorage.setItem('pending_active_tab', 'mine');
      logInfo('[AuthLoginView] Saved active tab for mine context');
    }

    // 直接跳转，不隐藏页面，不延迟
    // 之前支付流程可以直接弹出微信授权页面，说明不需要隐藏页面
    // 隐藏页面可能会阻止跳转，导致看不到微信授权页面
    logInfo('[AuthLoginView] About to call redirectToAuth', {
      context,
      hasPaymentInfo: !!paymentInfo,
      projectId: paymentInfo?.projectId,
      appId,
      timestamp: new Date().toISOString()
    });
    
  // 支付/一起捐场景：传递 projectId 用于授权回调后恢复业务流程
  // "我的"场景：不传递 projectId
    if (context === 'payment' && paymentInfo) {
      logInfo('[AuthLoginView] Calling redirectToAuth for payment', {
        appId,
        projectId: paymentInfo.projectId
      });
      WechatAuthService.redirectToAuth(appId, paymentInfo.projectId);
    } else if (context === 'team' && teamInfo?.projectId) {
      logInfo('[AuthLoginView] Calling redirectToAuth for team', {
        appId,
        projectId: teamInfo.projectId,
      });
      WechatAuthService.redirectToAuth(appId, teamInfo.projectId);
    } else {
      logInfo('[AuthLoginView] Calling redirectToAuth for mine', { appId });
      WechatAuthService.redirectToAuth(appId);
    }
    
    logInfo('[AuthLoginView] redirectToAuth called, page should redirect now');
  };

  return (
    <div className="auth-login-view">
      <div className="auth-login-container">
        {/* 机构 Logo */}
        {organization?.logoUrl && (
          <div className="auth-logo">
            <img src={organization.logoUrl} alt={organization.name || '机构Logo'} />
          </div>
        )}

        {/* 用户协议同意 */}
        <div className="auth-agreement">
          <label className="agreement-checkbox">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span className="agreement-text">
              我已阅读并同意
              <span className="agreement-link" onClick={onAgreementClick}>
                《用户协议》
              </span>
            </span>
          </label>
        </div>

        {/* 微信登录按钮 */}
        <button className="auth-wechat-btn" onClick={handleWechatLogin} disabled={!agreed}>
          <img src={wechatIcon} alt="微信" className="wechat-icon" />
          <span>微信登录</span>
        </button>
      </div>
    </div>
  );
};

